import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

// Localizar e substituir: destaca as ocorrencias, navega entre elas e troca
// uma ou todas. Sem diferenciar maiusculas/minusculas.
const searchKey = new PluginKey("documentSearch");

function findMatches(doc, term) {
    const results = [];
    if (!term) return results;
    const needle = term.toLowerCase();
    doc.descendants((node, pos) => {
        if (!node.isText) return;
        const text = node.text.toLowerCase();
        let index = text.indexOf(needle);
        while (index !== -1) {
            results.push({ from: pos + index, to: pos + index + term.length });
            index = text.indexOf(needle, index + needle.length);
        }
    });
    return results;
}

export const DocumentSearch = Extension.create({
    name: "documentSearch",

    addStorage() {
        return { term: "", results: [], current: 0 };
    },

    addCommands() {
        const storage = this.storage;
        const step = (direction) => ({ tr, dispatch }) => {
            if (!storage.results.length) return false;
            storage.current = (storage.current + direction + storage.results.length) % storage.results.length;
            if (dispatch) {
                const match = storage.results[storage.current];
                tr.setSelection(TextSelection.create(tr.doc, match.from, match.to)).scrollIntoView().setMeta(searchKey, true);
            }
            return true;
        };
        const replace = (tr, match, replacement) => {
            if (replacement) tr.insertText(replacement, match.from, match.to);
            else tr.delete(match.from, match.to);
        };

        return {
            setSearchTerm: (term) => ({ tr, dispatch }) => {
                storage.term = term || "";
                storage.current = 0;
                if (dispatch) tr.setMeta(searchKey, true);
                return true;
            },
            nextSearchResult: () => step(1),
            previousSearchResult: () => step(-1),
            replaceCurrent: (replacement) => ({ tr, dispatch }) => {
                const match = storage.results[storage.current];
                if (!match) return false;
                if (dispatch) replace(tr, match, replacement);
                return true;
            },
            replaceAll: (replacement) => ({ tr, dispatch }) => {
                if (!storage.results.length) return false;
                // De tras para frente para nao deslocar as posicoes.
                if (dispatch) [...storage.results].reverse().forEach((match) => replace(tr, match, replacement));
                return true;
            },
        };
    },

    addProseMirrorPlugins() {
        const extension = this;
        return [
            new Plugin({
                key: searchKey,
                state: {
                    init: () => DecorationSet.empty,
                    apply(tr, old) {
                        const storage = extension.storage;
                        if (!tr.docChanged && !tr.getMeta(searchKey)) return old.map(tr.mapping, tr.doc);
                        storage.results = findMatches(tr.doc, storage.term);
                        if (storage.current >= storage.results.length) storage.current = 0;
                        const decorations = storage.results.map((match, index) =>
                            Decoration.inline(match.from, match.to, { class: index === storage.current ? "doc-search-match doc-search-match--current" : "doc-search-match" }));
                        return DecorationSet.create(tr.doc, decorations);
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },
});

// Limpa HTML colado (principalmente do Word): tira classes, comentarios,
// tags do Office e estilos que o documento nao usa. A estrutura e o resto
// da formatacao ficam com o esquema do editor.
const KEEP_STYLES = ["font-weight", "font-style", "text-decoration", "text-align", "color", "background-color", "font-size", "margin-left", "text-indent", "line-height"];

export function cleanPastedHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("style, script, meta, link, title, xml").forEach((node) => node.remove());
    doc.querySelectorAll("*").forEach((element) => {
        if (/^o:|^w:|^v:/i.test(element.tagName)) {
            element.replaceWith(...element.childNodes);
            return;
        }
        element.removeAttribute("class");
        element.removeAttribute("lang");
        const style = element.getAttribute("style");
        if (style) {
            const kept = style.split(";")
                .map((rule) => rule.trim())
                .filter((rule) => rule && !/^mso-/i.test(rule) && KEEP_STYLES.includes(rule.split(":")[0].trim().toLowerCase()));
            if (kept.length) element.setAttribute("style", kept.join("; "));
            else element.removeAttribute("style");
        }
    });
    return doc.body.innerHTML.replace(/<!--[\s\S]*?-->/g, "");
}
